import FileSaver from "file-saver";
import { isSpecialBDCountryCodes, isSpecialCountryCode } from "./CountryHelper";
import { MAX_SEARCH_TERMS } from "../constants/offlineApp-constants";
import NumAbbr from "number-abbreviate";
import { EXCEL_FORMAT_ERR, EXCEL_UPLOAD_MAX_ERR_MSG, FILE_FORMAT, FILE_SIZE } from "./BulkUpdateModal.constant";
const currencySymbols = {
  vn: "₫",
  ph: "₱",
  th: "฿",
  bd: "TK",
  kh: "$",
  la: "LAK",
  id: "Rp",
  pk: "Rs",
};
export const getFileNameWithTimestamp = (fileName) => {
  const fileExtension = fileName
    .slice(((fileName.lastIndexOf(".") - 1) >>> 0) + 2)
    .toLowerCase();
  return `${fileName
    .split(".")
    .slice(0, -1)
    .join(".")}_${Date.now()}.${fileExtension}`;
};
export const filterNonBlankRows = (rows) => {
  const filteredRows = rows.filter((row) => {
    const columns = row.split(",").map((col) => col.trim());
    return columns.some((col) => col !== "");
  });
  return filteredRows.join("\n");
};
export const getNonBlankRowsFile = (rows, file) => {
  const filteredContent = filterNonBlankRows(rows);
  return new Blob([filteredContent], { type: file.type });
};
export const downloadTemplate = (
  fileBase64,
  type = "csv",
  fileName = "Template"
) => {
  let byteCharacters = atob(fileBase64);
  let bytesLength = byteCharacters.length;
  let size = 1024;
  let slicesCount = Math.ceil(bytesLength / size);
  let byteArrays = new Array(slicesCount);
  for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
    let start = sliceIndex * size;
    let end = Math.min(start + size, bytesLength);
    let bytes = new Array(end - start);
    for (let offset = start, i = 0; offset < end; ++i, ++offset) {
      bytes[i] = byteCharacters[offset].charCodeAt(0);
    }
    byteArrays[sliceIndex] = new Uint8Array(bytes);
  }
  FileSaver.saveAs(new Blob(byteArrays), `${fileName}.${type}`);
};
export const redirectIfInvalidConfig = ({
  configData,
  selectedCountryCode,
  offlineApp,
  history,
}) => {
  const isOfflineAppEnabled =
    configData?.common[selectedCountryCode]?.OfflineApp;
  if (!isOfflineAppEnabled || !offlineApp) {
    history.push("/adminconfigurationpage");
  }
};
export const isSalesRole = (currentRole) => {
  return currentRole === "Salesman" || currentRole === "Sales Officer";
};
export const shouldRenderOfflineApp = (
  configData,
  selectedCountryCode,
  offlineApp
) => {
  return configData?.common[selectedCountryCode]?.OfflineApp && offlineApp;
};
export const getHeaderNameWithCurrency = (column, selectedCountryCode) => {
  if (!column || !column.headerName || !selectedCountryCode) {
    return column ? column.headerName : "";
  }
  const currencySuffix = currencySymbols[selectedCountryCode] || "";
  const specialHeaders = [
    "Invoice SKU Price",
    "Invoice SKU Amount",
    "Invoice SKU Return Price",
    "Invoice SKU Return Amount",
  ];
  if (specialHeaders.includes(column.headerName)) {
    return `${column.headerName} (${currencySuffix})`;
  }
  return column.headerName;
};
export const getRoleBasedOnCountryCode = (countryCode) => {
  if (!countryCode) return "Branch Managers";
  if (isSpecialCountryCode(countryCode)) {
    return "RSD";
  }
  if (isSpecialBDCountryCodes(countryCode)) {
    return "AM";
  }
  return "Branch Managers";
};
export const getConditionEqualValue = (
  value1,
  value2,
  trueValue,
  falseValue
) => {
  return value1 === value2 ? trueValue : falseValue;
};
export const getConditionUnEqualValue = (
  value1,
  value2,
  trueValue,
  falseValue
) => {
  return value1 !== value2 ? trueValue : falseValue;
};
export const getConditionValue = (value, trueValue, falseValue) => {
  return value ? trueValue : falseValue;
};
export const getSearchOptions = (searchOptions, getOptions) => {
  return searchOptions?.length >= MAX_SEARCH_TERMS ? [] : getOptions();
};
export const getStartDateBySelection = (
  selectedValue,
  todaysDate,
  startDate,
  startDatePrev
) => {
  if (selectedValue === "day") {
    return todaysDate;
  } else if (selectedValue === "mtd") {
    return startDate;
  } else {
    return startDatePrev;
  }
};
export const getEndDateBySelection = (
  selectedValue,
  todaysDate,
  lastDate,
  lastDatePrev
) => {
  if (selectedValue === "day") {
    return todaysDate;
  } else if (selectedValue === "mtd") {
    return lastDate;
  } else {
    return lastDatePrev;
  }
};
export const getRoleLabel = (level, role, location, isDistributor) => {
  if (level && level !== role && location) {
    return isDistributor ? "Main Distributor" : "Main Team";
  }
  return "";
};
// Module-level NumAbbr instance to avoid recreation on every call
const NUM_ABBREVIATOR = new NumAbbr(["K", "M", "Bn", "Tr"]);

/**
* Returns currency symbol for selected country code.
* Reads from provided code first, then falls back to header-selected country in localStorage.
* @param {string} countryCode
* @returns {string}
*/
const getCurrencySymbolForCountry = (countryCode) => {
  const resolvedCountryCode = String(
    countryCode
      || (typeof window !== "undefined" && window?.localStorage
        ? window.localStorage.getItem("countryCode")
        : "")
      || "id"
  ).toLowerCase();
  return currencySymbols[resolvedCountryCode] || "";
};
/**
* Formats numeric values with abbreviation (K/M/Bn/Tr) for large numbers.
* Handles null, undefined, "NA", strings, objects, currency-like input, and preformatted K/M/Bn/Tr values.
* 
* @param {*} value - Number, string, object with value property, or special values (NA, null, undefined)
* @param {Object} options - Formatting options
* @param {number} options.abbreviateThreshold - Values >= this get abbreviated (default: 1000)
* @param {number} options.abbreviateDecimals - Decimals to show when abbreviating (default: 2)
* @param {boolean} options.preserveDecimalDistinction - For small decimal inputs, round to 2 decimals before trimming trailing zeros (default: true)
* @param {boolean} options.showCurrency - Prefix currency symbol based on selected country code (default: false)
* @param {string} options.countryCode - Optional country override; if omitted, reads from localStorage countryCode
* @returns {string} Formatted value
* 
* @example
* formatNumericValue(5000000)              // "5M"
* formatNumericValue(500)                  // "500"
* formatNumericValue("500.00")             // "500"
* formatNumericValue("12.50")              // "12.5"
* formatNumericValue("500")                // "500"
* formatNumericValue("NA")                 // "NA"
* formatNumericValue(null)                 // "0"
* formatNumericValue({value: 5000000})     // "5M"
* formatNumericValue(-5000000)             // "-5M"
* formatNumericValue("Rp 4,000,000")       // "4M"
*/
export const formatNumericValue = (value, options = {}) => {
  const {
    abbreviateThreshold = 1000,
    abbreviateDecimals = 2,
    preserveDecimalDistinction = true,
    showCurrency = false,
    countryCode,
  } = options;
  const currencySymbol = showCurrency ? getCurrencySymbolForCountry(countryCode) : "";
  const withCurrencyPrefix = (formattedValue) => {
    if (!showCurrency || !currencySymbol) return formattedValue;
    return `${currencySymbol} ${formattedValue}`;
  };
  // === NORMALIZE INPUT ===
  // Extract value from object if needed
  let rawValue = value;
  if (typeof value === "object" && value !== null) {
    rawValue = value.achievedPercentage ?? value.value ?? "";
  }
  const valueString = String(rawValue ?? "").trim();
  // === HANDLE SPECIAL CASES ===
  if (!valueString || valueString.toLowerCase() === "null") {
    return withCurrencyPrefix("0");
  }
  if (valueString.toUpperCase() === "NA" || valueString === "-") {
    return "NA";
  }
  // Check if value is already preformatted (e.g., "5000K", "2.5Bn")
  if (/^-?\d+(\.\d+)?(K|M|Bn|Tr)$/i.test(valueString)) {
    return withCurrencyPrefix(valueString);
  }
  // === CONVERT TO NUMBER ===
  const hadDecimal = valueString.includes(".");
  // Remove formatting/currency symbols while preserving digits, minus, and decimal point.
  const numericString = valueString
    .replace(/,/g, "")
    .replace(/%/g, "")
    .replace(/[^0-9.-]/g, "")
    .trim();
  const numericValue = Number(numericString);
  if (!Number.isFinite(numericValue)) {
    return "NA";
  }
  // === HANDLE ZERO ===
  if (numericValue === 0) {
    return withCurrencyPrefix("0");
  }
  // === ABBREVIATE OR FORMAT ===
  const absValue = Math.abs(numericValue);
  if (absValue >= abbreviateThreshold) {
    // Abbreviate large values (positive and negative)
    const abbreviatedValue = NUM_ABBREVIATOR.abbreviate(numericValue, abbreviateDecimals);
    return withCurrencyPrefix(abbreviatedValue
      .replace(/\.00(?=[A-Za-z]+$)/, "")
      .replace(/(\.\d)0(?=[A-Za-z]+$)/, "$1"));
  }
  // Small values: preserve decimal distinction if enabled
  if (preserveDecimalDistinction && hadDecimal) {
    const roundedValue = Number(numericValue.toFixed(2));
    return withCurrencyPrefix(String(roundedValue));
  }
  return withCurrencyPrefix(String(numericValue));
};
export const getPathValues = (pathArray) => {
  let array = pathArray?.sort(
      (a, b) =>
        parseInt(a.geoLocationSequence) - parseInt(b.geoLocationSequence)
    );;
  let RoundOffValue = 7;
  const createPoints = (points, roundoff) => {
    let croppedWaypoint = points.map((item) => {
      return {
        ...item,
        salesmanLatitudes: parseFloat(item.salesmanLatitudes).toFixed(
          roundoff
        ),
        salesmanLongitudes: parseFloat(item.salesmanLongitudes).toFixed(
          roundoff
        ),
      };
    });
    croppedWaypoint = [
      ...croppedWaypoint
        .reduce((map, { salesmanLatitudes, salesmanLongitudes }) => {
          return map.set(`${salesmanLatitudes}-${salesmanLongitudes}`, {
            salesmanLatitudes,
            salesmanLongitudes,
          });
        }, new Map())
        .values(),
    ];
    if(croppedWaypoint.length > 25){
      const newRound = roundoff - 1
      return createPoints(croppedWaypoint, newRound)
    }
    else{
      return croppedWaypoint
    }
  };
  if (array.length <= 25) {
    return array;
  } else {
    const newPoints = createPoints(array, RoundOffValue)
    return newPoints
  }
};
export const validateFileMeta = (file, setFileError) => {
  const ext = file.name.split(".").pop();
  if (ext !== "csv") {
    setFileError({ header: FILE_FORMAT, message: EXCEL_FORMAT_ERR });
    return false;
  }
  const fileSize = file.size / 1024 / 1024;
  if (!fileSize || fileSize > 25) {
    setFileError({ header: FILE_SIZE, message: EXCEL_UPLOAD_MAX_ERR_MSG });
    return false;
  }
  return true;
};
export const validateField = ({
  row,
  field,
  setFileError,
  resolve,
  validateFn,
  errorHeader,
  errorMessageFn
}) => {
  const value = row[field];
  if (!validateFn(value)) {
    setFileError({
      header: errorHeader,
      message: errorMessageFn(row),
    });
    resolve("invalid");
    return true;
  }
  return false;
};
//Safely splits a CSV row string into an array, handling quoted fields and escaped quotes.
export const splitCSVRow = (row) => {
  const result = [];
  let current = '';
  let inQuotes = false;
  let i = 0;
  while (i < row.length) {
    const char = row[i];
    const nextChar = row[i + 1];
    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++; // skip escaped quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
    i++;
  }
  result.push(current);
  return result;
};
export const formatCsvFile = (file, targetColumns = []) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      const rawContent = e.target.result;
      const lines = rawContent.split(/\r?\n/);
      if (!lines.length) return reject(new Error("Empty CSV file"));
       // === Remove only fully empty rows ===
      const cleanedLines = lines.filter((line, index) => {
        if (index === 0) return true; // keep header
        const cols = line.split(",").map((c) => c.trim());
        return cols.some((c) => c !== "");
      });
      const headers = cleanedLines[0].split(",");
      const columnIndexesToFormat = [];
      headers.forEach((header, index) => {
        const trimmedHeader = header.trim().toLowerCase();
        if (targetColumns.map(c => c.toLowerCase()).includes(trimmedHeader)) {
          columnIndexesToFormat.push(index);
        }
      });
      // Function to normalize any date to dd/mm/yyyy
      const normalizeDate = (val) => {
        if (!val) return val;
        const clean = val.trim().replace(/-/g, "/");
        const match = clean.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (match) {
          const [_, d, m, y] = match;
          return `${d.padStart(2, "0")}/${m.padStart(2, "0")}/${y}`;
        }
        return val;
      };
      const formattedLines = cleanedLines.map((line, lineIndex) => {
        if (lineIndex === 0) return line;
        const columns = line.split(",");
        columnIndexesToFormat.forEach((colIdx) => {
          if (columns[colIdx]) {
            columns[colIdx] = normalizeDate(columns[colIdx]);
          }
        });
        return columns.join(",");
      });
      const formattedContent = formattedLines.join("\n");
      const blob = new Blob([formattedContent], { type: "text/csv" });
      const updatedFile = new File([blob], file.name, { type: "text/csv" });
      resolve(updatedFile);
    };
    reader.onerror = (err) => reject(err);
    reader.readAsText(file);
  });
};
export const getGeoCodeComplianceValue = (params) => {
  return params.value === '1' || params.value === 'true' ? 'true' : 'false';
}
 